import * as React from "react";
import type { LaunchPlan } from "@/data/types";
import { Reveal, Overline } from "./primitives";

interface Props {
  plan: LaunchPlan;
}

export const LaunchTimeline: React.FC<Props> = ({ plan }) => {
  if (!plan?.phases?.length) return null;

  return (
    <section
      className="w-full bg-ivory-alt py-20 md:py-28"
      data-testid="launch-timeline"
      aria-label="The first 14 days"
    >
      <div className="mx-auto w-full max-w-5xl px-6 md:px-10">
        <Reveal>
          <Overline>The first 14 days</Overline>
          <h2 className="mt-4 max-w-2xl font-serif text-3xl leading-tight text-charcoal md:text-4xl">
            Prepare. Reintroduce. Create momentum. Respond.
          </h2>
          {plan.intro && (
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-charcoal-soft md:text-lg">
              {plan.intro}
            </p>
          )}
        </Reveal>

        <Reveal delay={0.05}>
          <ol className="mt-14 grid grid-cols-1 gap-y-8 sm:grid-cols-2 lg:grid-cols-5 lg:gap-x-4">
            {plan.phases.map((phase, i) => (
              <li key={i} className="relative lg:pr-4">
                <div className="flex items-center gap-3 lg:block">
                  <span className="font-serif text-lg text-brass">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="mt-0 hidden h-px w-full bg-charcoal/15 lg:mt-4 lg:block" />
                </div>
                <h3 className="mt-2 text-sm font-medium uppercase tracking-[0.16em] text-charcoal lg:mt-4">
                  {phase.label}
                </h3>
                {phase.detail && (
                  <p className="mt-2 text-sm leading-relaxed text-charcoal-soft">
                    {phase.detail}
                  </p>
                )}
              </li>
            ))}
          </ol>
        </Reveal>
      </div>
    </section>
  );
};
